import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { buildActionCards } from "../components/performance/action-center/buildActionCards";

const ActionCenterContext = createContext(null);

/**
 * Shared Action Center state for the layout (EO-1907).
 *
 * The nav badge count and the Action Center page header used to be two
 * independent counters that disagreed: the page deduped v1/v2 cards and hid
 * dismissed ones, the badge did neither. This provider makes them one number by
 *
 *  - holding the session-only "Maybe Later" dismissals in ONE place, so
 *    dismissing a card on the page also drops the nav badge, and
 *  - deriving `recommendationCount` from the SAME pure {@link buildActionCards}
 *    the page renders from — so dedup, PROMO_EXPIRY and dismissals are applied
 *    identically to both.
 *
 * The two data feeds stay owned by the hooks in the layout (passed in as props);
 * this provider only owns the dismiss set and the derived count.
 */
export const ActionCenterProvider = ({
  propertyId = null,
  data = null,
  v2Data = null,
  feedsSettled = false,
  children,
}) => {
  const [dismissedKeys, setDismissedKeys] = useState(() => new Set());

  const dismiss = useCallback((key) => {
    setDismissedKeys((prev) => {
      // Adding an existing key is a no-op — return the same Set so React skips
      // the re-render and StrictMode's double-invoke stays idempotent.
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Session-only dismissals must not leak across properties. A property switch
  // already full-reloads the app (useReloadOnPropertyChange), so this is
  // belt-and-suspenders. Reset ONLY on a real property→property change (both ids
  // present and different): a transient null (e.g. a property re-resolve) must
  // NOT wipe the user's dismissals and resurrect dismissed cards mid-session.
  const prevPropertyIdRef = useRef(propertyId);
  useEffect(() => {
    const prev = prevPropertyIdRef.current;
    prevPropertyIdRef.current = propertyId;
    if (prev != null && propertyId != null && prev !== propertyId) {
      setDismissedKeys(new Set());
    }
  }, [propertyId]);

  const descriptors = useMemo(
    () => buildActionCards({ data, v2Data, dismissedKeys }),
    [data, v2Data, dismissedKeys]
  );

  // Gate on both feeds settling, exactly as the page header does: until then the
  // count is 0 (NavItem renders no badge), so the final combined number appears
  // exactly once with no v1-only flash (EO-1834).
  const recommendationCount = useMemo(
    () => (feedsSettled ? descriptors.length : 0),
    [feedsSettled, descriptors]
  );

  const value = useMemo(
    () => ({ dismissedKeys, dismiss, recommendationCount }),
    [dismissedKeys, dismiss, recommendationCount]
  );

  return (
    <ActionCenterContext.Provider value={value}>
      {children}
    </ActionCenterContext.Provider>
  );
};

ActionCenterProvider.propTypes = {
  propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.object,
  v2Data: PropTypes.object,
  feedsSettled: PropTypes.bool,
  children: PropTypes.node,
};

/** The shared count + dismiss API. Throws outside a provider. */
export const useActionCenter = () => {
  const ctx = useContext(ActionCenterContext);
  if (!ctx) {
    throw new Error(
      "useActionCenter must be used within an ActionCenterProvider"
    );
  }
  return ctx;
};

/**
 * The shared dismiss state, or null when rendered outside a provider.
 * ActionCenter uses this so its own unit tests (which render it with no provider)
 * fall back to local dismiss state.
 */
export const useActionCenterOptional = () => useContext(ActionCenterContext);
